import { cn } from "@/lib/utils";
import { ArrayViz } from "@/components/viz/ArrayViz";
import { TreeViz } from "@/components/viz/TreeViz";
import { GraphViz } from "@/components/viz/GraphViz";
import { LinkedListViz } from "@/components/viz/LinkedListViz";
import { HashTableViz } from "@/components/viz/HashTableViz";
import { StackQueueViz } from "@/components/viz/StackQueueViz";
import { MatrixViz } from "@/components/viz/MatrixViz";
import type { ArrayStep, TreeStep, TreeNode, GraphStep, GraphNode, GraphEdge, LinkedListNode, LinkedListStep, HashTableStep, StackQueueStep, MatrixStep } from "@/components/viz/types";

interface TeachCardProps {
  data: {
    title: string;
    body: string;
    keyInsight: string;
    code?: string;
    language?: string;
    template?: string;
    initialData?: Record<string, unknown>;
    animationSteps?: Record<string, unknown>[];
  };
}

function renderBoldText(text: string) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

function renderAnimation(
  template: string,
  steps: Record<string, unknown>[],
  initialData?: Record<string, unknown>
) {
  const d = initialData ?? {};

  switch (template) {
    case "ArrayViz":
      return (
        <ArrayViz
          initialArray={(d.array as (number | string)[]) ?? []}
          extraArrays={(d.extraArrays as { label: string; values: (number | string)[] }[]) ?? []}
          steps={steps as unknown as ArrayStep[]}
        />
      );
    case "TreeViz":
      return (
        <TreeViz
          initialTree={(d.tree as TreeNode) ?? { id: 0, value: "" }}
          extraArrays={(d.extraArrays as { label: string; values: (number | string)[] }[]) ?? []}
          steps={steps as unknown as TreeStep[]}
        />
      );
    case "GraphViz":
      return (
        <GraphViz
          nodes={(d.nodes as GraphNode[]) ?? []}
          edges={(d.edges as GraphEdge[]) ?? []}
          directed={(d.directed as boolean) ?? false}
          extraArrays={(d.extraArrays as { label: string; values: (number | string)[] }[]) ?? []}
          steps={steps as unknown as GraphStep[]}
        />
      );
    case "LinkedListViz":
      return (
        <LinkedListViz
          initialList={(d.list as LinkedListNode[]) ?? []}
          doubly={(d.doubly as boolean) ?? false}
          steps={steps as unknown as LinkedListStep[]}
        />
      );
    case "HashTableViz":
      return (
        <HashTableViz
          bucketCount={(d.bucketCount as number) ?? 8}
          mode={(d.mode as "chaining" | "probing") ?? "chaining"}
          steps={steps as unknown as HashTableStep[]}
        />
      );
    case "StackQueueViz":
      return (
        <StackQueueViz
          variant={(d.variant as "stack" | "queue" | "deque") ?? "stack"}
          initialItems={(d.items as (number | string)[]) ?? []}
          steps={steps as unknown as StackQueueStep[]}
        />
      );
    case "MatrixViz":
      return (
        <MatrixViz
          initialMatrix={(d.matrix as (number | string)[][]) ?? []}
          rowLabels={(d.rowLabels as string[]) ?? undefined}
          colLabels={(d.colLabels as string[]) ?? undefined}
          steps={steps as unknown as MatrixStep[]}
        />
      );
    default:
      return null;
  }
}

export function TeachCard({ data }: TeachCardProps) {
  const { title, body, keyInsight, code, language, template, initialData, animationSteps } = data;

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-2xl font-bold leading-tight">{title}</h2>

      <p className="text-base leading-relaxed">
        {renderBoldText(body)}
      </p>

      {code && (
        <div className="relative rounded-lg bg-zinc-900 text-zinc-100">
          {language && (
            <span className="absolute right-3 top-2 text-xs font-medium uppercase tracking-wide text-zinc-400">
              {language}
            </span>
          )}
          <pre className="overflow-x-auto p-4 text-base leading-relaxed">
            <code>{code}</code>
          </pre>
        </div>
      )}

      {template && animationSteps && animationSteps.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4">
          {renderAnimation(template, animationSteps, initialData)}
        </div>
      )}

      <div
        className={cn(
          "rounded-md border-l-4 border-primary bg-primary/10 px-4 py-3"
        )}
      >
        <p className="mb-1 text-sm font-semibold text-primary">Key Insight</p>
        <p className="text-sm leading-relaxed">
          {renderBoldText(keyInsight)}
        </p>
      </div>
    </div>
  );
}
